import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import ChildrensBookManuscriptImportUnifiedReview from "./ChildrensBookManuscriptImportUnifiedReview";
import ChildrensBookManuscriptImportApplyPanel from "./ChildrensBookManuscriptImportApplyPanel";
import ChildrensBookImportedManuscriptResult from "./ChildrensBookImportedManuscriptResult";

import {
  normalizeChildrensBookManuscriptImportPreview,
} from "../utils/childrensBookManuscriptImportPreviewContract";

const MAX_DOCX_BYTES =
  4 * 1024 * 1024;

const MANUSCRIPT_IMPORT_RECOVERY_VERSION =
  3;

const MANUSCRIPT_IMPORT_RECOVERY_PREFIX =
  "nextscenes:childrens-book:manuscript-import-preview:";


export function manuscriptImportRecoveryKey() {
  if (
    typeof window ===
      "undefined"
  ) {
    return "";
  }

  const route =
    `${
      window.location
        ?.pathname || ""
    }${
      window.location
        ?.search || ""
    }`;

  return (
    MANUSCRIPT_IMPORT_RECOVERY_PREFIX +
    encodeURIComponent(
      route || "local"
    )
  );
}


function emptyRecovery() {
  return {
    preview: null,
    mappingBySection: {},
    decisionBySection: {},
  };
}


function readManuscriptImportRecovery(
  recoveryKey = ""
) {
  const key =
    recoveryKey ||
    manuscriptImportRecoveryKey();

  if (
    !key ||
    typeof window ===
      "undefined"
  ) {
    return emptyRecovery();
  }

  try {
    const raw =
      window.sessionStorage
        .getItem(key);

    if (!raw) {
      return emptyRecovery();
    }

    const parsed =
      JSON.parse(raw);

    if (
      parsed?.version !==
        MANUSCRIPT_IMPORT_RECOVERY_VERSION ||
      !parsed?.preview ||
      !Array.isArray(
        parsed.preview
          .sections
      )
    ) {
      window.sessionStorage
        .removeItem(key);

      return emptyRecovery();
    }

    const normalizedPreview =
      normalizeChildrensBookManuscriptImportPreview(
        parsed.preview
      );

    return {
      preview:
        normalizedPreview,

      mappingBySection:
        parsed
          .mappingBySection &&
        typeof parsed
          .mappingBySection ===
          "object" &&
        !Array.isArray(
          parsed
            .mappingBySection
        )
          ? parsed
              .mappingBySection
          : {},

      decisionBySection:
        parsed
          .decisionBySection &&
        typeof parsed
          .decisionBySection ===
          "object" &&
        !Array.isArray(
          parsed
            .decisionBySection
        )
          ? parsed
              .decisionBySection
          : {},
    };
  } catch (error) {
    try {
      window.sessionStorage
        .removeItem(key);
    } catch (
      storageError
    ) {
      // Recovery remains optional.
    }

    return emptyRecovery();
  }
}

function writeManuscriptImportRecovery({
  key,
  preview,
  mappingBySection = {},
  decisionBySection = {},
}) {
  if (
    !key ||
    typeof window ===
      "undefined"
  ) {
    return;
  }

  try {
    if (!preview) {
      window.sessionStorage
        .removeItem(key);

      return;
    }

    const normalizedPreview =
      normalizeChildrensBookManuscriptImportPreview(
        preview
      );

    const safeMapping =
      mappingBySection &&
      typeof mappingBySection ===
        "object" &&
      !Array.isArray(
        mappingBySection
      )
        ? mappingBySection
        : {};

    const safeDecisions =
      decisionBySection &&
      typeof decisionBySection ===
        "object" &&
      !Array.isArray(
        decisionBySection
      )
        ? decisionBySection
        : {};

    window.sessionStorage
      .setItem(
        key,
        JSON.stringify({
          version:
            MANUSCRIPT_IMPORT_RECOVERY_VERSION,

          preview:
            normalizedPreview,

          mappingBySection:
            safeMapping,

          decisionBySection:
            safeDecisions,
        })
      );
  } catch (error) {
    try {
      window.sessionStorage
        .removeItem(key);
    } catch (
      storageError
    ) {
      /*
       * Browser-tab recovery is helpful but optional.
       * The review remains usable when storage is unavailable.
       */
    }
  }
}


function clean(
  value
) {
  return value == null
    ? ""
    : String(value).trim();
}

function formatBytes(
  value
) {
  const bytes =
    Number(value || 0);

  if (
    !Number.isFinite(bytes) ||
    bytes <= 0
  ) {
    return "0 KB";
  }

  return `${Math.max(
    1,
    Math.round(
      bytes / 1024
    )
  ).toLocaleString()} KB`;
}

export default function ChildrensBookManuscriptImportPreview({
  lang = "en",
  bookMapUnits = [],
  manuscriptEntries = [],
  manuscriptPlan = null,
  onPreviewManuscriptImport,
  onApplyManuscriptImport,
  onContinueAfterImport,
}) {
  const isFR =
    String(lang)
      .toLowerCase() ===
    "fr";

  const [
    recoveryKey,
  ] = useState(
    () =>
      manuscriptImportRecoveryKey()
  );

  const [
    initialRecovery,
  ] = useState(
    () =>
      readManuscriptImportRecovery(
        recoveryKey
      )
  );

  const [
    busy,
    setBusy,
  ] = useState(false);

  const [
    completedImport,
    setCompletedImport,
  ] = useState(null);

  const [
    importFinished,
    setImportFinished,
  ] = useState(false);


  const [
    preview,
    setPreview,
  ] = useState(
    initialRecovery.preview
  );

  const [
    mappingBySection,
    setMappingBySection,
  ] = useState(
    initialRecovery
      .mappingBySection
  );

  const [
    decisionBySection,
    setDecisionBySection,
  ] = useState(
    initialRecovery
      .decisionBySection
  );

  const recoverySnapshotRef =
    useRef({
      preview:
        initialRecovery.preview,

      mappingBySection:
        initialRecovery
          .mappingBySection,

      decisionBySection:
        initialRecovery
          .decisionBySection,
    });

  const [
    notice,
    setNotice,
  ] = useState(
    () =>
      initialRecovery.preview
        ? {
            type:
              "success",

            message:
              isFR
                ? "Aperçu restauré après l’actualisation. Le fichier DOCX original n’est pas conservé."
                : "Preview restored after refresh. The original DOCX file is not stored.",
          }
        : {
            type: "",
            message: "",
          }
  );

  const ui = {
    title: isFR
      ? "Importer un manuscrit Word"
      : "Import a Word Manuscript",

    badge: isFR
      ? "Vérification avant import"
      : "Review before import",

    body: isFR
      ? "Sélectionnez un fichier DOCX pour examiner son texte, ses titres et ses sauts de page avant de choisir où placer chaque section dans le livre."
      : "Select a DOCX file to inspect its text, headings, and page breaks before choosing where each section belongs in the book.",

    choose: isFR
      ? "Choisir un manuscrit Word"
      : "Choose Word manuscript",

    reading: isFR
      ? "Lecture du manuscrit…"
      : "Reading manuscript…",

    ready: isFR
      ? "Aperçu prêt. Aucune donnée du projet n’a été modifiée."
      : "Preview ready. No project data has changed.",

    invalid: isFR
      ? "Choisissez un fichier Word au format DOCX."
      : "Choose a DOCX Word document.",

    tooLarge: isFR
      ? "Le document Word doit peser 4 Mo ou moins."
      : "The Word document must be 4 MB or smaller.",

    failed: isFR
      ? "Le manuscrit Word n’a pas pu être prévisualisé."
      : "The Word manuscript could not be previewed.",

    file: isFR
      ? "Fichier"
      : "File",

    words: isFR
      ? "Mots"
      : "Words",

    paragraphs: isFR
      ? "Paragraphes"
      : "Paragraphs",

    sections: isFR
      ? "Sections détectées"
      : "Detected sections",

    headings: isFR
      ? "Titres Word"
      : "Word headings",

    breaks: isFR
      ? "Sauts de page"
      : "Page breaks",

    discard: isFR
      ? "Supprimer cet aperçu"
      : "Discard preview",

    noApply: isFR
      ? "Rien n’a encore changé dans votre manuscrit. Le fichier DOCX original n’est pas conservé."
      : "Nothing has changed in your manuscript yet. The original DOCX file is not stored.",
  };

  useEffect(() => {
    const snapshot = {
      preview,

      mappingBySection:
        mappingBySection &&
        typeof mappingBySection ===
          "object" &&
        !Array.isArray(
          mappingBySection
        )
          ? mappingBySection
          : {},

      decisionBySection:
        decisionBySection &&
        typeof decisionBySection ===
          "object" &&
        !Array.isArray(
          decisionBySection
        )
          ? decisionBySection
          : {},
    };

    recoverySnapshotRef.current =
      snapshot;

    writeManuscriptImportRecovery({
      key:
        recoveryKey,

      ...snapshot,
    });
  }, [
    recoveryKey,
    preview,
    mappingBySection,
    decisionBySection,
  ]);


  function rememberMapping(
    nextMapping
  ) {
    const safeMapping =
      nextMapping &&
      typeof nextMapping ===
        "object" &&
      !Array.isArray(
        nextMapping
      )
        ? nextMapping
        : {};

    const snapshot = {
      ...recoverySnapshotRef
        .current,

      preview:
        recoverySnapshotRef
          .current.preview ||
        preview,

      mappingBySection:
        safeMapping,
    };

    recoverySnapshotRef.current =
      snapshot;

    setMappingBySection(
      safeMapping
    );

    writeManuscriptImportRecovery({
      key:
        recoveryKey,

      ...snapshot,
    });
  }


  function rememberDecisions(
    nextDecisions
  ) {
    const safeDecisions =
      nextDecisions &&
      typeof nextDecisions ===
        "object" &&
      !Array.isArray(
        nextDecisions
      )
        ? nextDecisions
        : {};

    const snapshot = {
      ...recoverySnapshotRef
        .current,

      preview:
        recoverySnapshotRef
          .current.preview ||
        preview,

      decisionBySection:
        safeDecisions,
    };

    recoverySnapshotRef.current =
      snapshot;

    setDecisionBySection(
      safeDecisions
    );

    writeManuscriptImportRecovery({
      key:
        recoveryKey,

      ...snapshot,
    });
  }


  async function handleFile(
    event
  ) {
    const file =
      event?.target
        ?.files?.[0];

    if (!file) return;

    const fileName =
      clean(file.name);

    if (
      !/\.docx$/i.test(
        fileName
      )
    ) {
      setPreview(null);
      setMappingBySection({});
      setDecisionBySection({});

      setNotice({
        type: "error",
        message:
          ui.invalid,
      });

      event.target.value =
        "";

      return;
    }

    if (
      Number(file.size || 0) >
        MAX_DOCX_BYTES
    ) {
      setPreview(null);
      setMappingBySection({});
      setDecisionBySection({});

      setNotice({
        type: "error",
        message:
          ui.tooLarge,
      });

      event.target.value =
        "";

      return;
    }

    if (
      typeof
        onPreviewManuscriptImport !==
      "function"
    ) {
      setPreview(null);
      setMappingBySection({});
      setDecisionBySection({});

      setNotice({
        type: "error",
        message:
          ui.failed,
      });

      event.target.value =
        "";

      return;
    }

    try {
      setBusy(true);
      setPreview(null);
      setMappingBySection({});
      setDecisionBySection({});

      setNotice({
        type: "working",
        message:
          ui.reading,
      });

      const response =
        await onPreviewManuscriptImport(
          file
        );

      const nextPreview =
        normalizeChildrensBookManuscriptImportPreview(
          response?.preview
        );

      const recoverySnapshot = {
        preview:
          nextPreview,

        mappingBySection:
          {},

        decisionBySection:
          {},
      };

      recoverySnapshotRef.current =
        recoverySnapshot;

      setPreview(
        nextPreview
      );

      writeManuscriptImportRecovery({
        key:
          recoveryKey,

        ...recoverySnapshot,
      });

      setNotice({
        type: "success",
        message:
          ui.ready,
      });
    } catch (error) {
      setPreview(null);
      setMappingBySection({});
      setDecisionBySection({});

      setNotice({
        type: "error",
        message:
          clean(
            error?.message
          ) ||
          ui.failed,
      });
    } finally {
      setBusy(false);

      if (
        event?.target
      ) {
        event.target.value =
          "";
      }
    }
  }

  function clearPreviewState(
    nextNotice = {
      type: "",
      message: "",
    }
  ) {
    const emptySnapshot = {
      preview: null,
      mappingBySection: {},
      decisionBySection: {},
    };

    recoverySnapshotRef.current =
      emptySnapshot;

    setPreview(null);
    setMappingBySection({});
    setDecisionBySection({});

    writeManuscriptImportRecovery({
      key:
        recoveryKey,

      ...emptySnapshot,
    });

    setNotice({
      type:
        clean(
          nextNotice?.type
        ),

      message:
        clean(
          nextNotice?.message
        ),
    });
  }


  function completeAppliedImport(
    outcome = {}
  ) {
    const completedPlan =
      (
        outcome
          ?.manuscriptPlan &&
        typeof
          outcome
            .manuscriptPlan ===
          "object" &&
        !Array.isArray(
          outcome
            .manuscriptPlan
        )
      )
        ? outcome
            .manuscriptPlan
        : (
            outcome
              ?.result
              ?.draft
              ?.manuscriptPlan &&
            typeof
              outcome
                .result
                .draft
                .manuscriptPlan ===
              "object"
          )
          ? outcome
              .result
              .draft
              .manuscriptPlan
          : (
              manuscriptPlan &&
              typeof
                manuscriptPlan ===
                "object" &&
              !Array.isArray(
                manuscriptPlan
              )
            )
            ? manuscriptPlan
            : {
                entries:
                  manuscriptEntries,
              };

    clearPreviewState();

    setImportFinished(
      true
    );

    setCompletedImport({
      ...outcome,

      manuscriptPlan:
        completedPlan,
    });
  }


  function continueAfterImport() {
    const outcome =
      completedImport;

    setCompletedImport(
      null
    );

    if (
      typeof
        onContinueAfterImport ===
        "function"
    ) {
      onContinueAfterImport(
        outcome
      );
    }
  }


  function discardPreview() {
    clearPreviewState();
  }

  if (completedImport) {
    return (
      <ChildrensBookImportedManuscriptResult
        lang={lang}
        bookMapUnits={
          bookMapUnits
        }
        manuscriptPlan={
          completedImport
            .manuscriptPlan
        }
        summary={
          completedImport
            .summary
        }
        message={
          completedImport
            .message
        }
        onContinue={
          continueAfterImport
        }
      />
    );
  }

  if (importFinished) {
    return null;
  }


  return (
    <section
      className="ns-card ns-nbb-major-panel"
      aria-labelledby="manuscript-import-preview-title"
      style={{
        padding: 20,
        borderRadius: 18,
        border:
          "1px solid rgba(37,99,235,0.20)",
        background:
          "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
        marginBottom: 18,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems:
            "flex-start",
          justifyContent:
            "space-between",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            maxWidth: 720,
          }}
        >
          <h2
            id="manuscript-import-preview-title"
            style={{
              margin: 0,
              fontSize: 21,
            }}
          >
            {ui.title}
          </h2>

          <p
            className="ns-card-meta"
            style={{
              margin:
                "7px 0 0",
              lineHeight: 1.55,
            }}
          >
            {ui.body}
          </p>
        </div>

        <span
          style={{
            display:
              "inline-flex",
            alignItems:
              "center",
            minHeight: 30,
            padding:
              "5px 10px",
            borderRadius: 999,
            background:
              "#dbeafe",
            color:
              "#1d4ed8",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {ui.badge}
        </span>
      </div>

      <div
        style={{
          marginTop: 16,
          display: "flex",
          alignItems:
            "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <label
          style={{
            display:
              "inline-grid",
            gap: 6,
            fontWeight: 800,
          }}
        >
          <span>
            {busy
              ? ui.reading
              : ui.choose}
          </span>

          <input
            type="file"
            aria-label={
              ui.choose
            }
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            disabled={
              busy
            }
            onChange={
              handleFile
            }
          />
        </label>
      </div>

      {notice.message ? (
        <div
          role={
            notice.type ===
            "error"
              ? "alert"
              : "status"
          }
          aria-live="polite"
          style={{
            marginTop: 14,
            padding:
              "11px 13px",
            borderRadius: 12,
            background:
              notice.type ===
              "error"
                ? "#fef2f2"
                : notice.type ===
                  "success"
                ? "#ecfdf5"
                : "#eff6ff",
            border:
              notice.type ===
              "error"
                ? "1px solid #fecaca"
                : notice.type ===
                  "success"
                ? "1px solid #a7f3d0"
                : "1px solid #bfdbfe",
          }}
        >
          {notice.message}
        </div>
      ) : null}

      {preview ? (
        <div
          style={{
            marginTop: 18,
            display: "grid",
            gap: 16,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(130px, 1fr))",
              gap: 10,
            }}
          >
            {[
              [
                ui.file,
                `${
                  clean(
                    preview.fileName
                  )
                } · ${
                  formatBytes(
                    preview.sizeBytes
                  )
                }`,
              ],
              [
                ui.words,
                Number(
                  preview.wordCount ||
                    0
                ).toLocaleString(),
              ],
              [
                ui.paragraphs,
                Number(
                  preview.paragraphCount ||
                    0
                ).toLocaleString(),
              ],
              [
                ui.sections,
                Number(
                  preview.sectionCount ||
                    0
                ).toLocaleString(),
              ],
              [
                ui.headings,
                Number(
                  preview.headingCount ||
                    0
                ).toLocaleString(),
              ],
              [
                ui.breaks,
                Number(
                  preview.pageBreakCount ||
                    0
                ).toLocaleString(),
              ],
            ].map(
              ([
                label,
                value,
              ]) => (
                <div
                  key={
                    label
                  }
                  className="ns-nbb-inner-panel"
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    background:
                      "#ffffff",
                    border:
                      "1px solid #dbeafe",
                  }}
                >
                  <div
                    className="ns-nbb-detail-label"
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      color:
                        "#64748b",
                    }}
                  >
                    {label}
                  </div>

                  <div
                    style={{
                      marginTop: 5,
                      fontWeight: 700,
                      overflowWrap:
                        "anywhere",
                    }}
                  >
                    {value}
                  </div>
                </div>
              )
            )}
          </div>



          <ChildrensBookManuscriptImportUnifiedReview
            lang={lang}
            sections={
              preview.sections
            }
            bookMapUnits={
              bookMapUnits
            }
            manuscriptEntries={
              manuscriptEntries
            }
            mappingBySection={
              mappingBySection
            }
            decisionBySection={
              decisionBySection
            }
            onMappingChange={
              rememberMapping
            }
            onDecisionChange={
              rememberDecisions
            }
          />

          <ChildrensBookManuscriptImportApplyPanel
            lang={lang}
            sections={
              preview.sections
            }
            bookMapUnits={
              bookMapUnits
            }
            manuscriptPlan={
              manuscriptPlan &&
              typeof manuscriptPlan ===
                "object" &&
              !Array.isArray(
                manuscriptPlan
              )
                ? manuscriptPlan
                : {
                    entries:
                      manuscriptEntries,
                  }
            }
            mappingBySection={
              mappingBySection
            }
            decisionBySection={
              decisionBySection
            }
            onApplyManuscriptImport={
              onApplyManuscriptImport
            }
            onApplied={
              completeAppliedImport
            }
          />

          <div
            style={{
              display: "flex",
              alignItems:
                "center",
              justifyContent:
                "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.5,
                maxWidth: 700,
              }}
            >
              {ui.noApply}
            </p>

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={
                discardPreview
              }
            >
              {ui.discard}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
